import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import API from '../utils/api';
import { siteUrl } from '../utils/siteUrl';
import { openCheckoutUrl, openExternalUrl } from '../utils/twa';
import { copyToClipboard } from '../utils/clipboard';
import {
  trackAffiliatePageViewed,
  trackAffiliateLinkCopied,
  trackAffiliateCodeCopied,
  trackAffiliateConnectStarted,
  trackAffiliateConnectCompleted,
  trackAffiliateStripeDashboardOpened,
  trackAffiliateProfileSaved,
} from '../utils/analytics';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const StatCard = ({ label, value, sub, color }) => (
  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '14px' }}>
    <div className="card-body p-3 text-center">
      <div style={{ fontSize: '24px', fontWeight: 700, color: color || '#2d1b69' }}>{value}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted2)', marginTop: '2px' }}>{label}</div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>{sub}</div>}
    </div>
  </div>
);

const AffiliateDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectSuccess, setConnectSuccess] = useState(false);

  const [affiliate, setAffiliate]           = useState(null);
  const [conversions, setConversions]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [connectLoading, setConnectLoading]   = useState(false);
  const [loginLinkLoading, setLoginLinkLoading] = useState(false);
  const [copied, setCopied]                 = useState(false);
  const [codeCopied, setCodeCopied]         = useState(false);
  const [error, setError]                   = useState('');
  const [profileEdit, setProfileEdit]       = useState(false);
  const [profileForm, setProfileForm]       = useState({ bio: '', websiteUrl: '', promoMethod: '' });
  const [profileSaving, setProfileSaving]   = useState(false);
  const [profileMsg, setProfileMsg]         = useState('');

  const loadData = useCallback(async () => {
    try {
      const [affRes, convRes] = await Promise.all([
        API.get('/affiliate/me'),
        API.get('/affiliate/me/conversions'),
      ]);
      const aff = affRes.data.affiliate;
      setAffiliate(aff);
      setConversions(convRes.data.conversions || []);
      setProfileForm({ bio: aff.bio || '', websiteUrl: aff.websiteUrl || '', promoMethod: aff.promoMethod || '' });
    } catch (err) {
      if (err.response?.status === 404) navigate('/affiliate/apply');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadData(); trackAffiliatePageViewed(); }, [loadData]);

  // Handle Stripe Connect return — called once when ?connect=success lands
  useEffect(() => {
    const connect = searchParams.get('connect');
    if (connect === 'success') {
      // Clear the param immediately so this doesn't re-run on future renders
      const next = new URLSearchParams(searchParams);
      next.delete('connect');
      setSearchParams(next, { replace: true });
      // Tell backend to verify onboarding status then refresh affiliate data
      API.get('/affiliate/connect/return')
        .then(res => {
          if (res.data.onboarded) {
            setConnectSuccess(true);
            trackAffiliateConnectCompleted();
          }
          return loadData();
        })
        .catch(() => loadData());
    }
  }, []); // intentionally empty — only run once on mount

  const handleConnectStripe = async () => {
    setConnectLoading(true);
    setError('');
    trackAffiliateConnectStarted();
    try {
      const res = await API.post('/affiliate/connect/onboard');
      openCheckoutUrl(res.data.url);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start Stripe onboarding.');
      setConnectLoading(false);
    }
  };

  // siteUrl(), not window.location.origin — inside the apps the origin is
  // "https://localhost", which produced affiliate links nobody could open.
  const affiliateLink = () => `${siteUrl()}/?aff=${affiliate.code}`;

  const copyLink = async () => {
    // copyToClipboard falls back to execCommand where navigator.clipboard is
    // missing — which is the case in the Facebook/Instagram in-app browsers that
    // affiliate links are most often opened in.
    if (await copyToClipboard(affiliateLink())) {
      setCopied(true);
      trackAffiliateLinkCopied();
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Don't claim "Copied!" when nothing reached the clipboard.
      setError('Could not copy automatically — please copy the link manually.');
    }
  };

  const copyCode = async () => {
    const code = affiliate.promoCodeName || affiliate.code;
    if (await copyToClipboard(code)) {
      setCodeCopied(true);
      trackAffiliateCodeCopied();
      setTimeout(() => setCodeCopied(false), 2000);
    } else {
      setError('Could not copy automatically — please copy the code manually.');
    }
  };

  const handleOpenStripeDashboard = async () => {
    setLoginLinkLoading(true);
    try {
      const res = await API.get('/affiliate/connect/login-link');
      trackAffiliateStripeDashboardOpened();
      // openExternalUrl, not window.open: inside the apps window.open('_blank')
      // is a no-op, so this button silently did nothing on iOS/Android.
      openExternalUrl(res.data.url);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not open Stripe dashboard.');
    } finally {
      setLoginLinkLoading(false);
    }
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileMsg('');
    try {
      const res = await API.patch('/affiliate/me', profileForm);
      setAffiliate(res.data.affiliate);
      setProfileEdit(false);
      trackAffiliateProfileSaved();
      setProfileMsg('Profile updated.');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      setProfileMsg(err.response?.data?.message || 'Failed to save.');
    } finally {
      setProfileSaving(false);
    }
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
    </div>
  );

  if (!affiliate) return null;

  const isPending = affiliate.status === 'pending';
  const isActive  = affiliate.status === 'active';
  const { stats } = affiliate;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', padding: '32px 24px 24px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <Link to="/dashboard" style={{ color: '#c9a8ff', fontSize: '13px', textDecoration: 'none' }}>
            ← Back to Dashboard
          </Link>
          <h1 style={{ color: '#fff', fontFamily: "'Fredoka One',cursive", fontSize: '28px', margin: '12px 0 4px' }}>
            Affiliate Dashboard
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '14px', margin: 0 }}>
            {affiliate.code
              ? <><span>Code: </span><strong style={{ color: '#fff' }}>{affiliate.code}</strong>&nbsp;·&nbsp;</>
              : <><span style={{ color: '#fbbf24' }}>Code pending assignment</span>&nbsp;·&nbsp;</>
            }
            {isActive ? <span style={{ color: '#4ade80' }}>● Active</span> : <span style={{ color: '#fbbf24' }}>● Pending Review</span>}
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '28px 24px' }}>

        {isPending && (
          <div className="alert rounded-3 mb-4" style={{ background: '#fef9c3', border: '1px solid #fde047', color: '#713f12', fontSize: '14px' }}>
            ⏳ Your application is under review. We'll activate your account within 24–48 hours.
          </div>
        )}

        {error && (
          <div className="alert alert-danger rounded-3 mb-4" style={{ fontSize: '14px' }}>{error}</div>
        )}

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3"><StatCard label="Clicks" value={stats.clicks} color="#6f42c1" /></div>
          <div className="col-6 col-md-3"><StatCard label="Signups" value={stats.signups} color="#0d6efd" /></div>
          <div className="col-6 col-md-3"><StatCard label="Conversions" value={stats.conversions} color="#059669" /></div>
          <div className="col-6 col-md-3"><StatCard label="Pending Payout" value={fmt(stats.commissionPending)} color="#ea580c" /></div>
        </div>

        <div className="row g-3 mb-4">
          <div className="col-6"><StatCard label="Total Earned" value={fmt(stats.commissionEarned)} sub="all time" color="#2d1b69" /></div>
          <div className="col-6"><StatCard label="Total Paid Out" value={fmt(stats.commissionPaid)} sub="all time" color="#059669" /></div>
        </div>

        {/* Referral link + promo code */}
        {isActive && (
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              {/* Referral link */}
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Your referral link</h6>
              {affiliate.code ? (
                <div className="d-flex gap-2 align-items-center mb-4">
                  <input
                    className="form-control rounded-3"
                    readOnly
                    value={affiliateLink()}
                    style={{ fontSize: '13px', background: 'var(--page-bg)', color: 'var(--text-main)', fontWeight: 500 }}
                  />
                  <button
                    className="btn rounded-pill fw-bold px-3 flex-shrink-0"
                    style={{ background: copied ? '#059669' : '#6f42c1', color: 'white', fontSize: '13px', whiteSpace: 'nowrap' }}
                    onClick={copyLink}
                  >
                    {copied ? '✓ Copied' : 'Copy Link'}
                  </button>
                </div>
              ) : (
                <div className="alert rounded-3 mb-4" style={{ background: '#fef9c3', border: '1px solid #fde047', color: '#713f12', fontSize: '13px' }}>
                  Your affiliate code hasn't been assigned yet. We'll email you as soon as it's ready.
                </div>
              )}

              {/* Promo code */}
              <h6 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Your promo code</h6>
              {affiliate.stripePromotionCodeId ? (
                <>
                  <div className="d-flex gap-2 align-items-center mb-2 flex-wrap">
                    <div style={{
                      background: 'var(--purple-section)', border: '2px dashed #c9a8ff', borderRadius: '10px',
                      padding: '10px 20px', fontSize: '20px', fontWeight: 700, letterSpacing: '4px', color: 'var(--text-main)',
                    }}>
                      {affiliate.promoCodeName || affiliate.code}
                    </div>
                    <div>
                      <span style={{
                        fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                        background: affiliate.promoCodeActive ? '#dcfce7' : '#fee2e2',
                        color: affiliate.promoCodeActive ? '#059669' : '#dc2626',
                        display: 'block', marginBottom: '6px',
                      }}>
                        {affiliate.promoCodeActive ? '● Active' : '● Deactivated'}
                      </span>
                      <button
                        className="btn rounded-pill fw-bold px-3"
                        style={{ background: codeCopied ? '#059669' : '#6f42c1', color: 'white', fontSize: '13px' }}
                        onClick={copyCode}
                        disabled={!affiliate.promoCodeActive}
                      >
                        {codeCopied ? '✓ Copied' : 'Copy Code'}
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>
                    {affiliate.promoCodeActive
                      ? `Your audience gets ${affiliate.discountPercent}% off when they use this code at checkout.`
                      : 'This promo code has been deactivated. Contact us for a new one.'}
                    {' '}You earn <strong>{affiliate.commissionPercent}%</strong> commission on each conversion for up to 12 months.
                  </div>
                </>
              ) : (
                <p style={{ fontSize: '13px', color: 'var(--text-soft)' }}>
                  No promo code yet — we'll assign one to you. Your referral link still works for tracking.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Profile */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>Your profile</h6>
              {!profileEdit && (
                <button className="btn btn-sm rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '12px', fontWeight: 600 }}
                  onClick={() => setProfileEdit(true)}>Edit</button>
              )}
            </div>
            {profileMsg && <div className="alert alert-success rounded-3 py-2" style={{ fontSize: '13px' }}>{profileMsg}</div>}
            {profileEdit ? (
              <>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Website / Social URL</label>
                  <input className="form-control rounded-3" style={{ fontSize: '14px' }}
                    value={profileForm.websiteUrl}
                    onChange={e => setProfileForm(f => ({ ...f, websiteUrl: e.target.value }))}
                    placeholder="https://instagram.com/yourhandle" />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>About your audience</label>
                  <textarea className="form-control rounded-3" rows={3} style={{ fontSize: '14px' }}
                    value={profileForm.bio}
                    onChange={e => setProfileForm(f => ({ ...f, bio: e.target.value }))}
                    placeholder="Describe your audience..." />
                </div>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>Promo method</label>
                  <input className="form-control rounded-3" style={{ fontSize: '14px' }}
                    value={profileForm.promoMethod}
                    onChange={e => setProfileForm(f => ({ ...f, promoMethod: e.target.value }))}
                    placeholder="e.g. Instagram, TikTok, blog" />
                </div>
                <div className="d-flex gap-2">
                  <button className="btn rounded-pill fw-bold px-4" style={{ background: '#6f42c1', color: 'white', fontSize: '13px' }}
                    onClick={handleProfileSave} disabled={profileSaving}>
                    {profileSaving ? <span className="spinner-border spinner-border-sm me-2"></span> : null}Save
                  </button>
                  <button className="btn rounded-pill px-4" style={{ background: 'var(--header-bg)', color: 'var(--text-muted2)', fontSize: '13px' }}
                    onClick={() => setProfileEdit(false)}>Cancel</button>
                </div>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: 'var(--text-body)' }}>
                {affiliate.websiteUrl && <div className="mb-1"><span style={{ color: 'var(--text-soft)' }}>Website: </span><a href={affiliate.websiteUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1' }}>{affiliate.websiteUrl}</a></div>}
                {affiliate.bio && <div className="mb-1"><span style={{ color: 'var(--text-soft)' }}>Audience: </span>{affiliate.bio}</div>}
                {affiliate.promoMethod && <div><span style={{ color: 'var(--text-soft)' }}>Promo method: </span>{affiliate.promoMethod}</div>}
                {!affiliate.websiteUrl && !affiliate.bio && !affiliate.promoMethod && (
                  <p style={{ color: 'var(--text-soft)' }}>No profile info yet. Click Edit to add.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stripe Connect */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Payout method</h6>
            <p style={{ color: 'var(--text-muted2)', fontSize: '13px', marginBottom: '16px' }}>
              Connect your bank account via Stripe to receive payouts directly. Payouts are sent when your pending balance reaches ${affiliate.payoutThreshold}.
            </p>
            {connectSuccess && (
              <div className="alert alert-success rounded-3 py-2 px-3 mb-3" style={{ fontSize: '13px' }}>
                🎉 Bank account connected successfully! You're all set to receive payouts.
              </div>
            )}
            {affiliate.stripeConnectOnboarded ? (
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2" style={{ color: '#059669', fontSize: '14px', fontWeight: 600 }}>
                  <i className="bi bi-check-circle-fill"></i> Bank account connected — payouts active
                </div>
                <button
                  className="btn btn-sm rounded-pill fw-semibold px-3"
                  style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '13px', border: 'none' }}
                  onClick={handleOpenStripeDashboard}
                  disabled={loginLinkLoading}
                >
                  {loginLinkLoading
                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Opening…</>
                    : <><i className="bi bi-box-arrow-up-right me-1"></i>Manage on Stripe</>}
                </button>
              </div>
            ) : (
              <button
                className="btn rounded-pill fw-bold px-4"
                style={{ background: 'linear-gradient(135deg,#635bff,#4f46e5)', color: 'white', fontSize: '14px' }}
                onClick={handleConnectStripe}
                disabled={connectLoading || !isActive}
              >
                {connectLoading
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Redirecting…</>
                  : <><i className="bi bi-bank me-2"></i>Connect Bank via Stripe</>}
              </button>
            )}
            {!isActive && (
              <div style={{ color: 'var(--text-soft)', fontSize: '12px', marginTop: '8px' }}>
                Available once your account is approved.
              </div>
            )}
          </div>
        </div>

        {/* Payout summary */}
        {conversions.some(c => c.status === 'paid') && (
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Payout history</h6>
              <div className="table-responsive">
                <table className="table table-sm" style={{ fontSize: '13px' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-muted2)' }}>
                      <th>Paid date</th>
                      <th>Type</th>
                      <th>Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversions.filter(c => c.status === 'paid').map(c => (
                      <tr key={c._id}>
                        <td>{fmtDate(c.paidAt)}</td>
                        <td style={{ textTransform: 'capitalize' }}>{c.type} {c.plan ? `(${c.plan})` : ''}</td>
                        <td style={{ color: '#059669', fontWeight: 600 }}>{fmt(c.commissionAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Conversions table */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-main)' }}>Conversion history</h6>
            {conversions.length === 0 ? (
              <p style={{ color: 'var(--text-soft)', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
                No conversions yet. Start sharing your link!
              </p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm" style={{ fontSize: '13px' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-muted2)' }}>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Revenue</th>
                      <th>Commission</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversions.map(c => (
                      <tr key={c._id}>
                        <td>{fmtDate(c.convertedAt)}</td>
                        <td style={{ textTransform: 'capitalize' }}>{c.type} {c.plan ? `(${c.plan})` : ''}</td>
                        <td>{fmt(c.revenueAmount)}</td>
                        <td style={{ color: '#059669', fontWeight: 600 }}>{fmt(c.commissionAmount)}</td>
                        <td>
                          <span style={{
                            fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px',
                            background: c.status === 'paid' ? '#dcfce7' : '#fef9c3',
                            color: c.status === 'paid' ? '#059669' : '#713f12',
                          }}>
                            {c.status === 'paid' ? '✓ Paid' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboardPage;
